import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Search,
  Edit,
  Check,
  Copy,
  FileDown,
  Printer,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { showPostSuccess, showPostError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { ListLoader } from '@/components/shared/ListLoader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { refundsService } from '@/services/refundsService'
import { RefundListItem, RefundVendor } from '@/api/types'
import { calendarToday, formatCalendarDate } from '@/lib/calendarDate'
import { SortOrderToggle } from '@/components/shared/SortOrderToggle'
import type { ListSortDir } from '@/lib/listSort'

export const RefundsList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [refunds, setRefunds] = useState<RefundListItem[]>([])
  const [vendors, setVendors] = useState<RefundVendor[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value))
    setPageNumber(1) // Reset to first page when page size changes
  }
  const [searchQuery, setSearchQuery] = useState('')
  const [sortDir, setSortDir] = useState<ListSortDir>('desc')
  const [selectedVendorId, setSelectedVendorId] = useState<number | undefined>(undefined)
  const [refundDateFrom, setRefundDateFrom] = useState<string>('')
  const [refundDateTo, setRefundDateTo] = useState<string>('')
  const [dueDateFrom, setDueDateFrom] = useState<string>('')
  const [dueDateTo, setDueDateTo] = useState<string>('')
  const [postingRefundId, setPostingRefundId] = useState<number | null>(null)

  const branchId = user?.selectedBranch?.branchId

  // Load vendors for filter
  useEffect(() => {
    if (!branchId) return
    const loadVendors = async () => {
      try {
        const vendorList = await refundsService.getAllVendors(branchId)
        setVendors(vendorList)
      } catch (error: any) {
        // Error handled silently
      }
    }
    loadVendors()
  }, [branchId])

  // Load refunds
  useEffect(() => {
    if (!branchId) {
      return
    }

    const loadRefunds = async () => {
      try {
        setLoading(true)
        const response = await refundsService.getRefunds(branchId, pageNumber, pageSize, sortDir)
        const list = response.data || []
        setRefunds(list)
        setTotalCount(response.totalRecords || list.length || 0)
        const pages = response.totalRecords ? Math.ceil(response.totalRecords / response.pageSize) : 0
        setTotalPages(pages || 1)
      } catch (error: any) {
        const status = error.response?.status
        const msg = (error.response?.data?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records/.test(msg)
        setRefunds([])
        setTotalCount(0)
        setTotalPages(1)
        if (!isNoData) {
          toast.error(error.response?.data?.message || t('refunds.failedToLoadRefunds'))
        }
      } finally {
        setLoading(false)
      }
    }

    loadRefunds()
  }, [branchId, pageNumber, pageSize, sortDir])

  const filteredRefunds = useMemo(() => {
    return refunds.filter((refund) => {
      if (selectedVendorId && refund.vendorId !== selectedVendorId) {
        return false
      }

      if (refundDateFrom && refund.refundDate && refund.refundDate < refundDateFrom) {
        return false
      }

      if (refundDateTo && refund.refundDate && refund.refundDate > refundDateTo) {
        return false
      }

      if (dueDateFrom && refund.dueDate && refund.dueDate < dueDateFrom) {
        return false
      }

      if (dueDateTo && refund.dueDate && refund.dueDate > dueDateTo) {
        return false
      }

      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase()
        return (
          (refund.refundNo || refund.refundNumber || '').toLowerCase().includes(query) ||
          (refund.billNo || refund.billNumber || '').toLowerCase().includes(query) ||
          (refund.vendorName || '').toLowerCase().includes(query)
        )
      }

      return true
    })
  }, [refunds, searchQuery, selectedVendorId, refundDateFrom, refundDateTo, dueDateFrom, dueDateTo])

  const handleAddNew = () => {
    navigate('/purchases/refunds/new')
  }

  const handleEdit = (refund: RefundListItem) => {
    const refundId = refund.refundId
    if (!refundId) {
      toast.error('Invalid refund ID')
      return
    }
    const isPosted = refund.isPosted === true || refund.isPosted === 1
    if (isPosted) {
      toast.error(t('refunds.postedRefundCannotUpdate'))
      return
    }
    navigate(`/purchases/refunds/${refundId}/edit`, { state: { refund } })
  }

  const handleCopy = () => {
    const tableData = filteredRefunds.map(r => ({
      'Cr Note No': r.refundNo || r.refundNumber || '-',
      'Bill No': r.billNo || r.billNumber || '-',
      Vendor: r.vendorName || '-',
      Date: r.refundDate || '-',
      'Due Date': r.dueDate || '-',
      Amount: r.amount || r.total || 0,
    }))
    const csv = [
      Object.keys(tableData[0] || {}).join(','),
      ...tableData.map(row => Object.values(row).join(','))
    ].join('\n')
    navigator.clipboard.writeText(csv)
    toast.success(t('refunds.copiedToClipboard'))
  }

  const handleExportCSV = () => {
    const tableData = filteredRefunds.map(r => ({
      'Cr Note No': r.refundNo || r.refundNumber || '-',
      'Bill No': r.billNo || r.billNumber || '-',
      Vendor: r.vendorName || '-',
      Date: r.refundDate || '-',
      'Due Date': r.dueDate || '-',
      Amount: r.amount || r.total || 0,
    }))
    const csv = [
      Object.keys(tableData[0] || {}).join(','),
      ...tableData.map(row => Object.values(row).join(','))
    ].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `refunds_${calendarToday()}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success(t('refunds.exportedToCsv'))
  }

  const formatDate = (dateString?: string) => formatCalendarDate(dateString)

  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch {
      return '0.0.0.0'
    }
  }

  const handlePostRefund = async (refund: RefundListItem) => {
    const refundId = refund.refundId
    if (!refundId) {
      toast.error(t('refunds.invalidRefundId'))
      return
    }
    const isPosted = refund.isPosted === true || refund.isPosted === 1
    if (isPosted) {
      toast.error(t('refunds.alreadyPosted'))
      return
    }

    try {
      setPostingRefundId(refundId)
      const ipAddress = await getUserIPAddress()
      const response = await refundsService.postRefund(refundId, ipAddress)
      const result = response?.result || response
      if (result?.isError) {
        await showPostError(result)
        return
      }
      await showPostSuccess(t('refunds.postSuccess'))
      const list = await refundsService.getRefunds(branchId!, pageNumber, pageSize, sortDir)
      setRefunds(list.data || [])
    } catch (error: any) {
      await showPostError(error)
    } finally {
      setPostingRefundId(null)
    }
  }

  const handlePrintRefund = (refundId?: number) => {
    if (!refundId) {
      toast.error(t('refunds.invalidRefundId'))
      return
    }
    navigate(`/purchases/refunds/${refundId}/print`)
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('refunds.title')}
        description={t('refunds.manageRefunds')}
        action={{
          label: t('refunds.addNew'),
          onClick: handleAddNew,
          icon: Plus,
        }}
      />

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">{t('refunds.refundDateFrom')}</label>
              <Input
                type="date"
                value={refundDateFrom}
                onChange={(e) => setRefundDateFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t('refunds.refundDateTo')}</label>
              <Input
                type="date"
                value={refundDateTo}
                onChange={(e) => setRefundDateTo(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t('refunds.dueDateFrom')}</label>
              <Input
                type="date"
                value={dueDateFrom}
                onChange={(e) => setDueDateFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t('refunds.dueDateTo')}</label>
              <Input
                type="date"
                value={dueDateTo}
                onChange={(e) => setDueDateTo(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t('refunds.vendor')}</label>
              <Select
                value={selectedVendorId ? String(selectedVendorId) : 'all'}
                onValueChange={(value) => setSelectedVendorId(value === 'all' ? undefined : Number(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('refunds.all')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('refunds.all')}</SelectItem>
                  {vendors.map((vendor) => (
                    <SelectItem key={vendor.vendorId || vendor.id} value={String(vendor.vendorId || vendor.id)}>
                      {vendor.vendorName || vendor.vendorNameEn || vendor.vendorNameAr || `Vendor ${vendor.vendorId || vendor.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-4 mt-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('refunds.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <SortOrderToggle
                value={sortDir}
                onChange={(next) => {
                  setSortDir(next)
                  setPageNumber(1)
                }}
              />
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="h-4 w-4 mr-2" />
                {t('refunds.copy')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <FileDown className="h-4 w-4 mr-2" />
                {t('refunds.csv')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <ListLoader message={t('refunds.loadingRefunds')} />
          ) : filteredRefunds.length === 0 ? (
            <EmptyState
              title={t('refunds.noRefundsFound')}
              description={t('refunds.getStarted')}
              action={{
                label: t('refunds.addNewRefundAction'),
                onClick: handleAddNew,
              }}
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('refunds.crNoteNo')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('refunds.billNo')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('refunds.vendor')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('refunds.date')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('refunds.dueDate')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('refunds.amount')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('refunds.posted')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('refunds.print')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('common.edit')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('refunds.post')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRefunds.map((refund) => {
                      const refundId = refund.refundId
                      const refundNo = refund.refundNo || refund.refundNumber || refund.refundId || '-'
                      const billNo = refund.billNo || refund.billNumber || refund.billId || '-'
                      const vendorName = refund.vendorName || refund.vendorNameEn || refund.vendorNameAr || '-'
                      const isPosted = refund.isPosted === true || refund.isPosted === 1
                      return (
                        <TableRow key={refundId} className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors">
                          <TableCell className="px-4 py-2 text-sm text-center align-middle font-medium">
                            {refundNo}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {billNo}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {vendorName}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {formatDate(refund.refundDate)}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {formatDate(refund.dueDate)}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {refund.amount || refund.total || 0}
                          </TableCell>
                          <TableCell
                            className={`px-4 py-2 text-sm text-center align-middle ${
                              isPosted ? 'text-blue-800 dark:text-blue-300' : 'text-sky-500 dark:text-sky-400'
                            }`}
                          >
                            {isPosted ? t('refunds.posted') : t('refunds.notPosted')}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-center align-middle">
                            <div className="flex justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePrintRefund(refundId)}
                                className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                              >
                                <Printer className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-2 text-center align-middle">
                            <div className="flex justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(refund)}
                                disabled={isPosted}
                                className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-2 text-center align-middle">
                            <div className="flex justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePostRefund(refund)}
                                disabled={isPosted || postingRefundId === refundId}
                                title={isPosted ? t('refunds.alreadyPosted') : t('refunds.post')}
                                className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {(totalPages > 1 || totalCount > 0) && (
                <div className="flex items-center justify-between px-6 py-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    {t('common.showing')} {((pageNumber - 1) * pageSize) + 1} {t('common.to')} {Math.min(pageNumber * pageSize, totalCount)} {t('common.of')} {totalCount} {t('common.entries')}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground whitespace-nowrap">{t('common.pageSize')}:</span>
                      <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                        <SelectTrigger className="w-[100px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                      disabled={pageNumber === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      {t('common.previous')}
                    </Button>
                    <div className="text-sm">
                      {t('common.page')} {pageNumber} {t('common.of')} {totalPages || 1}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(p => Math.min(totalPages, p + 1))}
                      disabled={pageNumber >= totalPages}
                    >
                      {t('common.next')}
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
